import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Chart } from 'primereact/chart';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Knob } from 'primereact/knob';
import { Panel } from 'primereact/panel';
import { Timeline } from 'primereact/timeline';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import productConfiguratorMockService from '../../../services/mockData/productConfiguratorMockData';
import './style.scss';

const ProductDashboard = () => {
  const [productTemplates, setProductTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templates, analyticsData] = await Promise.all([
        productConfiguratorMockService.getProductTemplates(),
        productConfiguratorMockService.getProductAnalytics()
      ]);
      setProductTemplates(templates);
      setAnalytics(analyticsData);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load product data'
      });
    } finally {
      setLoading(false);
    }
  };

  const statusBodyTemplate = (rowData) => {
    const severity = rowData.status === 'Active' ? 'success' : 'warning';
    return <Tag value={rowData.status} severity={severity} />;
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-primary"
          tooltip="Configure Product"
          onClick={() => navigate(`/product-configurator/template/${rowData.id}`)}
        />
        <Button
          icon="pi pi-copy"
          className="p-button-rounded p-button-text"
          tooltip="Clone Product"
        />
        <Button
          icon="pi pi-chart-line"
          className="p-button-rounded p-button-text"
          tooltip="View Analytics"
        />
      </div>
    );
  };

  const categoryBreakdownChart = {
    labels: Object.keys(analytics?.categoryBreakdown || {}),
    datasets: [{
      data: Object.values(analytics?.categoryBreakdown || {}).map(c => c.percentage),
      backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
      hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF']
    }]
  };

  const performanceChart = {
    labels: analytics?.performanceTrend?.map(t => t.month) || [],
    datasets: [
      {
        label: 'Premium (in millions)',
        data: analytics?.performanceTrend?.map(t => t.premium / 1000000) || [],
        borderColor: '#42A5F5',
        fill: false,
        yAxisID: 'y1'
      },
      {
        label: 'Loss Ratio %',
        data: analytics?.performanceTrend?.map(t => t.lossRatio) || [],
        borderColor: '#FFA726',
        fill: false,
        yAxisID: 'y2'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top'
      }
    },
    scales: {
      y1: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Premium (PHP millions)'
        }
      },
      y2: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Loss Ratio %'
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };

  const filteredTemplates = selectedCategory === 'All'
    ? productTemplates
    : productTemplates.filter(p => p.category === selectedCategory);

  const categories = ['All', 'Motor', 'Health', 'Property', 'Travel', 'Marine'];

  return (
    <div className="product-dashboard">
      <Toast ref={toast} />

      <div className="dashboard-header">
        <h2>Product Configurator Dashboard</h2>
        <div className="header-actions">
          <Button
            label="Create New Product"
            icon="pi pi-plus"
            className="p-button-primary"
            onClick={() => navigate('/product-configurator/create')}
          />
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <Card>
            <div className="stat-content">
              <span className="stat-label">Active Products</span>
              <span className="stat-value">
                {productTemplates.filter(p => p.status === 'Active').length}
              </span>
              <span className="stat-change positive">+12% from last month</span>
            </div>
          </Card>
        </div>
        <div className="stat-card">
          <Card>
            <div className="stat-content">
              <span className="stat-label">Total Premium</span>
              <span className="stat-value">₱1.02B</span>
              <span className="stat-change positive">+8.3% YTD</span>
            </div>
          </Card>
        </div>
        <div className="stat-card">
          <Card>
            <div className="stat-content">
              <span className="stat-label">Avg Loss Ratio</span>
              <span className="stat-value">61.5%</span>
              <span className="stat-change positive">-3.2% improved</span>
            </div>
          </Card>
        </div>
        <div className="stat-card">
          <Card>
            <div className="stat-content">
              <span className="stat-label">Avg Commission</span>
              <span className="stat-value">16.8%</span>
              <span className="stat-change neutral">No change</span>
            </div>
          </Card>
        </div>
      </div>

      <TabView>
        <TabPanel header="Product Templates" leftIcon="pi pi-list">
          <div className="template-controls">
            <div className="left-controls">
              <Dropdown
                value={selectedCategory}
                options={categories}
                onChange={(e) => setSelectedCategory(e.value)}
                placeholder="Filter by Category"
                className="category-filter"
              />
            </div>
            <div className="right-controls">
              <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search products..."
                />
              </span>
            </div>
          </div>

          <DataTable
            value={filteredTemplates}
            paginator
            rows={10}
            loading={loading}
            globalFilter={globalFilter}
            className="product-table"
          >
            <Column field="templateCode" header="Product Code" sortable />
            <Column field="name" header="Product Name" sortable />
            <Column field="category" header="Category" sortable />
            <Column field="lineOfBusiness" header="Line of Business" sortable />
            <Column field="version" header="Version" sortable />
            <Column field="status" header="Status" body={statusBodyTemplate} sortable />
            <Column field="baseRate" header="Base Rate %" sortable />
            <Column field="commissionRate" header="Commission %" sortable />
            <Column header="Actions" body={actionBodyTemplate} />
          </DataTable>
        </TabPanel>

        <TabPanel header="Performance Analytics" leftIcon="pi pi-chart-line">
          <div className="analytics-grid">
            <div className="chart-container">
              <Card title="Premium & Loss Ratio Trend">
                <Chart type="line" data={performanceChart} options={chartOptions} />
              </Card>
            </div>
            <div className="chart-container">
              <Card title="Category Distribution">
                <Chart type="doughnut" data={categoryBreakdownChart} />
              </Card>
            </div>
          </div>

          <Card title="Top Performing Products" className="top-products-card">
            <DataTable value={analytics?.topProducts || []}>
              <Column field="productName" header="Product" />
              <Column field="totalPolicies" header="Policies" sortable
                body={(rowData) => rowData.totalPolicies.toLocaleString()} />
              <Column field="totalPremium" header="Premium" sortable
                body={(rowData) => `₱${(rowData.totalPremium / 1000000).toFixed(1)}M`} />
              <Column field="lossRatio" header="Loss Ratio" sortable
                body={(rowData) => (
                  <div className="loss-ratio-cell">
                    <ProgressBar
                      value={rowData.lossRatio}
                      showValue={false}
                      style={{ height: '20px' }}
                      color={rowData.lossRatio > 70 ? '#f44336' :
                             rowData.lossRatio > 60 ? '#ff9800' : '#4caf50'}
                    />
                    <span>{rowData.lossRatio}%</span>
                  </div>
                )} />
              <Column field="growth" header="Growth" sortable
                body={(rowData) => (
                  <Tag
                    value={`${rowData.growth > 0 ? '+' : ''}${rowData.growth}%`}
                    severity={rowData.growth > 0 ? 'success' : 'danger'}
                  />
                )} />
            </DataTable>
          </Card>
        </TabPanel>

        <TabPanel header="Quick Actions" leftIcon="pi pi-bolt">
          <div className="quick-actions-grid">
            <Card title="Product Management" className="action-card">
              <div className="action-list">
                <Button
                  label="Create New Product"
                  icon="pi pi-plus"
                  className="p-button-text action-button"
                  onClick={() => navigate('/product-configurator/create')}
                />
                <Button
                  label="Manage Coverages"
                  icon="pi pi-shield"
                  className="p-button-text action-button"
                  onClick={() => navigate('/product-configurator/coverages')}
                />
                <Button
                  label="Configure Rating Rules"
                  icon="pi pi-calculator"
                  className="p-button-text action-button"
                  onClick={() => navigate('/product-configurator/rating')}
                />
              </div>
            </Card>

            <Card title="Underwriting & Compliance" className="action-card">
              <div className="action-list">
                <Button
                  label="Underwriting Rules"
                  icon="pi pi-check-circle"
                  className="p-button-text action-button"
                  onClick={() => navigate('/product-configurator/underwriting')}
                />
                <Button
                  label="Document Templates"
                  icon="pi pi-file"
                  className="p-button-text action-button"
                  onClick={() => navigate('/product-configurator/documents')}
                />
                <Button
                  label="Approval Workflows"
                  icon="pi pi-sitemap"
                  className="p-button-text action-button"
                  onClick={() => navigate('/product-configurator/workflows')}
                />
              </div>
            </Card>

            <Card title="Market & Distribution" className="action-card">
              <div className="action-list">
                <Button
                  label="Market Mapping"
                  icon="pi pi-map"
                  className="p-button-text action-button"
                  onClick={() => navigate('/product-configurator/market-mapping')}
                />
                <Button
                  label="Commission Setup"
                  icon="pi pi-percentage"
                  className="p-button-text action-button"
                  onClick={() => navigate('/product-configurator/commissions')}
                />
                <Button
                  label="Product Analytics"
                  icon="pi pi-chart-bar"
                  className="p-button-text action-button"
                  onClick={() => navigate('/product-configurator/analytics')}
                />
              </div>
            </Card>
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default ProductDashboard;